import { OidcUserProfile } from '../types/oidc';
import { extractRolesFromToken, extractGroupsFromToken } from './jwtHelper';

export interface UserRolesAndGroups {
  groups: string[];
  roles: string[];
}

/**
 * Extrahiert Gruppen und Rollen aus den OIDC User-Daten
 * Prüft sowohl ID Token (profile) als auch Access Token
 */
export function extractUserRolesAndGroups(
  userData: { profile?: unknown; access_token?: string } | null | undefined
): UserRolesAndGroups {
  const groups = new Set<string>();
  const roles = new Set<string>();

  if (!userData) {
    return { groups: [], roles: [] };
  }

  // 1. Aus dem ID Token Profile extrahieren
  const userProfile = userData.profile as OidcUserProfile | undefined;

  if (userProfile?.groups) {
    userProfile.groups.forEach(group => groups.add(group));
  }

  if (userProfile?.roles) {
    userProfile.roles.forEach(role => roles.add(role));
  }

  if (userProfile?.realm_access?.roles) {
    userProfile.realm_access.roles.forEach(role => roles.add(role));
  }

  // 2. Aus dem Access Token extrahieren (falls vorhanden)
  if (userData.access_token) {
    try {
      const accessTokenGroups = extractGroupsFromToken(userData.access_token);
      accessTokenGroups.forEach(group => groups.add(group));

      const accessTokenRoles = extractRolesFromToken(userData.access_token);
      accessTokenRoles.forEach(role => roles.add(role));
    } catch (error) {
      console.warn('Fehler beim Extrahieren von Rollen aus Access Token:', error);
    }
  }

  return {
    groups: Array.from(groups),
    roles: Array.from(roles),
  };
}

/**
 * Prüft ob ein Benutzer eine der erlaubten Gruppen oder Rollen hat
 */
export function hasRequiredAccess(
  userData: { profile?: unknown; access_token?: string } | null | undefined,
  allowedGroups: string[] = [],
  allowedRoles: string[] = []
): boolean {
  const { groups, roles } = extractUserRolesAndGroups(userData);

  const hasAllowedGroup = allowedGroups.length === 0 ||
                         allowedGroups.some(group => groups.includes(group));

  const hasAllowedRole = allowedRoles.length === 0 ||
                        allowedRoles.some(role => roles.includes(role));

  return hasAllowedGroup || hasAllowedRole;
}
